import React, { useState } from 'react';
import { useTranslate, useNotify, useDataProvider } from 'react-admin';
import { useCanAccess } from '@react-admin/ra-rbac';
import {
    Card,
    CardContent,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    Typography,
    CircularProgress,
    Alert,
    Divider,
    Link,
} from '@mui/material';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import SearchIcon from '@mui/icons-material/Search';

const ACTIVATION_CODE_REGEX =
    /^[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}$/;

interface ActivationCodeResult {
    status: 'activated' | 'not_activated' | 'not_found';
    activated?: string;
    isbn?: string;
    product_name?: string;
    image?: string;
    subscription_guid?: string;
    user?: {
        guid?: string;
        email?: string;
        firstName?: string;
        lastName?: string;
    };
}

const ActivationCodeSearch = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const dataProvider = useDataProvider();
    const { canAccess, isPending } = useCanAccess({
        action: 'activationcode',
        resource: 'subscription',
    });

    const [code, setCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [result, setResult] = useState<ActivationCodeResult | null>(null);

    if (isPending || !canAccess) return null;

    const handleSearch = async () => {
        const normalized = code.trim().toUpperCase();
        if (!ACTIVATION_CODE_REGEX.test(normalized)) {
            notify(
                <Alert severity="warning">
                    {translate(
                        'resources.subscription.filters.activationcode_invalid',
                        {
                            _: 'Aktivointikoodin muoto on virheellinen (esim. YWA4-32HF-YFVJ-9EK4-B4R7)',
                        }
                    )}
                </Alert>
            );
            return;
        }

        setLoading(true);
        try {
            const { data } = await (dataProvider as any).getAny(
                `subscription/activationcode/${encodeURIComponent(normalized)}`,
                ''
            );
            setResult(data as ActivationCodeResult);
            setDialogOpen(true);
        } catch (err: any) {
            if (err?.status === 400) {
                notify(
                    <Alert severity="warning">
                        {translate(
                            'resources.subscription.filters.activationcode_invalid',
                            {
                                _: 'Aktivointikoodin muoto on virheellinen',
                            }
                        )}
                    </Alert>
                );
            } else if (err?.status === 403) {
                notify(
                    <Alert severity="error">
                        {translate('ra.auth.auth_check_error', {
                            _: 'Ei käyttöoikeutta',
                        })}
                    </Alert>
                );
            } else {
                notify(
                    <Alert severity="error">
                        {translate('ra.notification.http_error', {
                            _: 'Virhe haussa',
                        })}
                    </Alert>
                );
            }
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSearch();
        }
    };

    const getDialogTitle = () => {
        if (!result) return '';
        switch (result.status) {
            case 'activated':
                return translate(
                    'resources.subscription.filters.activationcode_activated_title',
                    {
                        _: 'Aktivointikoodi - aktivoitu',
                    }
                );
            case 'not_activated':
                return translate(
                    'resources.subscription.filters.activationcode_not_activated_title',
                    {
                        _: 'Aktivointikoodi - ei aktivoitu',
                    }
                );
            case 'not_found':
                return translate(
                    'resources.subscription.filters.activationcode_not_found_title',
                    {
                        _: 'Aktivointikoodi - ei löytynyt',
                    }
                );
            default:
                return '';
        }
    };

    const getAlertSeverity = (): 'success' | 'info' | 'warning' => {
        if (!result) return 'info';
        switch (result.status) {
            case 'activated':
                return 'success';
            case 'not_activated':
                return 'info';
            case 'not_found':
                return 'warning';
            default:
                return 'info';
        }
    };

    const getAlertMessage = () => {
        if (!result) return '';
        switch (result.status) {
            case 'activated':
                return translate(
                    'resources.subscription.filters.activationcode_found',
                    {
                        _: 'Aktivointikoodi on käytetty ja tilaus löytyi',
                    }
                );
            case 'not_activated':
                return translate(
                    'resources.subscription.filters.activationcode_not_activated',
                    {
                        _: 'Tuotetta ei ole vielä otettu käyttöön',
                    }
                );
            case 'not_found':
                return translate(
                    'resources.subscription.filters.activationcode_not_found',
                    {
                        _: 'Aktivointikoodilla ei löydy tilausta Storialta',
                    }
                );
            default:
                return '';
        }
    };

    return (
        <>
            <Card
                sx={{
                    display: { xs: 'none', md: 'block' },
                    mt: 1,
                }}
            >
                <CardContent sx={{ pt: 1, '&:last-child': { pb: 1 } }}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ mb: 1 }}
                    >
                        <VpnKeyIcon fontSize="small" color="action" />
                        <Typography variant="subtitle2" color="text.secondary">
                            {translate(
                                'resources.subscription.filters.activationcode',
                                {
                                    _: 'Aktivointikoodi',
                                }
                            )}
                        </Typography>
                    </Stack>
                    <TextField
                        value={code}
                        onChange={e => setCode(e.target.value)}
                        onKeyDown={handleKeyDown}
                        size="small"
                        fullWidth
                        placeholder={translate(
                            'resources.subscription.filters.activationcode_search',
                            { _: 'Syötä aktivointikoodi' }
                        )}
                        variant="outlined"
                        sx={{ mb: 1 }}
                    />
                    <Button
                        variant="contained"
                        size="small"
                        fullWidth
                        onClick={handleSearch}
                        disabled={!code.trim() || loading}
                        startIcon={
                            loading ? (
                                <CircularProgress size={16} />
                            ) : (
                                <SearchIcon />
                            )
                        }
                    >
                        {translate('ra.action.search', { _: 'Hae' })}
                    </Button>
                </CardContent>
            </Card>

            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: '8px' } }}
            >
                <DialogTitle>{getDialogTitle()}</DialogTitle>
                <DialogContent>
                    {result && (
                        <Stack direction="column" spacing={2} sx={{ pt: 1 }}>
                            <Alert severity={getAlertSeverity()}>
                                {getAlertMessage()}
                            </Alert>

                            {result.status !== 'not_found' && (
                                <>
                                    <Divider />
                                    <Stack
                                        direction="row"
                                        spacing={2}
                                        alignItems="flex-start"
                                    >
                                        {result.image && (
                                            <img
                                                src={result.image}
                                                alt={result.product_name || ''}
                                                style={{
                                                    width: '120px',
                                                    minWidth: '120px',
                                                    maxHeight: '170px',
                                                    objectFit: 'contain',
                                                }}
                                            />
                                        )}
                                        <Stack direction="column" spacing={1}>
                                            {result.isbn && (
                                                <Typography variant="body2">
                                                    <strong>ISBN:</strong>{' '}
                                                    {result.isbn}
                                                </Typography>
                                            )}
                                            {result.product_name && (
                                                <Typography variant="body2">
                                                    <strong>
                                                        {translate(
                                                            'resources.product.fields.name',
                                                            { _: 'Tuote' }
                                                        )}
                                                        :
                                                    </strong>{' '}
                                                    {result.product_name}
                                                </Typography>
                                            )}
                                        </Stack>
                                    </Stack>
                                </>
                            )}

                            {result.status === 'activated' && (
                                <>
                                    <Divider />
                                    {result.activated && (
                                        <Typography variant="body2">
                                            <strong>
                                                {translate(
                                                    'resources.subscription.filters.activationcode_activated_at',
                                                    {
                                                        _: 'Aktivoitu',
                                                    }
                                                )}
                                                :
                                            </strong>{' '}
                                            {new Date(
                                                result.activated
                                            ).toLocaleString('fi-FI')}
                                        </Typography>
                                    )}
                                    {result.user && (
                                        <>
                                            {(result.user.firstName ||
                                                result.user.lastName) && (
                                                <Typography variant="body2">
                                                    <strong>
                                                        {translate(
                                                            'resources.user.fields.name',
                                                            { _: 'Käyttäjä' }
                                                        )}
                                                        :
                                                    </strong>{' '}
                                                    {[
                                                        result.user.firstName,
                                                        result.user.lastName,
                                                    ]
                                                        .filter(Boolean)
                                                        .join(' ')}
                                                </Typography>
                                            )}
                                            {result.user.email && (
                                                <Typography variant="body2">
                                                    <strong>
                                                        {translate(
                                                            'resources.user.fields.email',
                                                            { _: 'Sähköposti' }
                                                        )}
                                                        :
                                                    </strong>{' '}
                                                    {result.user.email}
                                                </Typography>
                                            )}
                                        </>
                                    )}
                                    {result.subscription_guid && (
                                        <Typography variant="body2">
                                            <strong>
                                                {translate(
                                                    'resources.subscription.name_one',
                                                    { _: 'Tilaus' }
                                                )}
                                                :
                                            </strong>{' '}
                                            <Link
                                                href={`#/subscription/${result.subscription_guid}`}
                                            >
                                                {result.subscription_guid}
                                            </Link>
                                        </Typography>
                                    )}
                                </>
                            )}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setDialogOpen(false)}
                        color="primary"
                    >
                        {translate('ra.action.close')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ActivationCodeSearch;
